import { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import {
  doc,
  updateDoc,
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";

const BATCH_SIZE = 10;

export const useDataProcessingProforma = (data = [], isActiveUpdateProf) => {
  const [progressBilling, setProgress] = useState(0);

  // Buffers para resumen final (no causan re-render)
  const statsRef = useRef({
    successOriginal: [],
    successAlt: [],
    notFoundOriginal: [],
    notFoundBoth: [],
    altUpdateFailed: [],
    hardErrors: [],
    invalidArgument: [], // ← nuevos: Firestore "invalid-argument"
    emptyId: [], // ← nuevos: BICCIID vacío/espacios
  });

  useEffect(() => {
    if (!isActiveUpdateProf || !data || data.length === 0) return;

    let isCancelled = false;

    const updateOrderDocument = async (docId, billingData, meta = {}) => {
      try {
        const orderRef = doc(db, process.env.REACT_APP_COL_ORDERS, docId);
        await updateDoc(orderRef, {
          ...billingData,
          billingCurrencyCode: "CLP",
          billingCurrencySymbol: "$",
        });
        console.info(`✔️ [OK] updateDoc id=${docId}`);
        return { ok: true, code: null };
      } catch (err) {
        const code = err?.code || "unknown";
        const msg = err?.message || String(err);

        if (code === "invalid-argument") {
          // Guarda todos los detalles para poder identificar luego
          statsRef.current.invalidArgument.push({
            triedId: docId,
            typeofId: typeof docId,
            length: (docId ?? "").length,
            index: meta.index, // índice absoluto del dataset
            batchIndex: meta.batchIndex, // posición dentro del batch
            bicciidRaw: meta.bicciidRaw, // valor crudo recibido
          });
          console.error(
            `❌ [INVALID-ARG] id="${docId}" index=${meta.index}`,
            err
          );
        } else {
          console.error(
            `❌ [ERR] id="${docId}" index=${meta.index} code=${code}`,
            err
          );
        }

        return { ok: false, code, msg };
      }
    };

    const findAlternativeDocId = async (bicciid) => {
      const s = String(bicciid ?? "").trim();
      if (!s) return null; // no busques alternativo si viene vacío

      const collectionRef = collection(db, process.env.REACT_APP_COL_ORDERS);
      const fields = ["orderID", "key", "int_lpn", "soc"];

      // Como string
      for (const field of fields) {
        try {
          const q = query(collectionRef, where(field, "==", s));
          const snapshot = await getDocs(q);
          if (!snapshot.empty) {
            const id = snapshot.docs[0].id;
            console.info(`🔎 [ALT] id=${id} by=${field} value="${s}"`);
            return { id, byField: field, value: s };
          }
        } catch (e) {
          console.warn(`⚠️ [ALT-QUERY-ERR] field=${field} value="${s}" ->`, e);
        }
      }

      // Como número (por si el campo está guardado numérico)
      const maybeNumber = Number(s);
      if (!Number.isNaN(maybeNumber)) {
        for (const field of fields) {
          try {
            const q = query(collectionRef, where(field, "==", maybeNumber));
            const snapshot = await getDocs(q);
            if (!snapshot.empty) {
              const id = snapshot.docs[0].id;
              console.info(
                `🔎 [ALT] id=${id} by=${field} value(num)=${maybeNumber}`
              );
              return { id, byField: field, value: String(maybeNumber) };
            }
          } catch (e) {
            console.warn(
              `⚠️ [ALT-QUERY-ERR] field=${field} value(num)=${maybeNumber} ->`,
              e
            );
          }
        }
      }

      return null;
    };

    const processInBatches = async () => {
      // Limpia buffers por si re-ejecuta
      statsRef.current = {
        successOriginal: [],
        successAlt: [],
        notFoundOriginal: [],
        notFoundBoth: [],
        altUpdateFailed: [],
        hardErrors: [],
        invalidArgument: [],
        emptyId: [],
      };

      for (let i = 0; i < data.length; i += BATCH_SIZE) {
        if (isCancelled) break;

        const batch = data.slice(i, i + BATCH_SIZE);

        const tasks = batch.map(async (item, idx) => {
          const {
            BICCIID,
            PaymentValor,
            PaymentType_service,
            PaymentProforma,
          } = item;

          const bicciidRaw = BICCIID;
          const docId = String(bicciidRaw ?? "");
          const docIdTrim = docId.trim();

          const billingData = {
            billingPaymentValor: PaymentValor,
            billingPaymentType_service: PaymentType_service,
            billingPaymentProforma: PaymentProforma,
          };

          // Log de inicio por ítem
          console.groupCollapsed(
            `⏩ [ITEM] BICCIID=${docIdTrim || "(vacío)"} (batchIndex=${
              i + idx
            })`
          );
          console.debug("payload:", { docId: docIdTrim, billingData });

          // Si viene vacío/espacios, registra y salta
          if (!docIdTrim) {
            statsRef.current.emptyId.push({
              index: i + idx,
              bicciidRaw,
            });
            console.warn(
              `🟡 [EMPTY-ID] index=${i + idx} BICCIID="${bicciidRaw}"`
            );
            console.groupEnd();
            return;
          }

          // 1) Intento directo
          const res = await updateOrderDocument(docIdTrim, billingData, {
            index: i + idx,
            batchIndex: idx,
            bicciidRaw,
          });

          if (res.ok) {
            statsRef.current.successOriginal.push(docIdTrim);
            console.groupEnd();
            return;
          }

          if (res.code === "not-found") {
            console.warn(
              `🟡 [NOT-FOUND] id=${docIdTrim} -> buscando alternativo...`
            );
            statsRef.current.notFoundOriginal.push(docIdTrim);

            // 2) Alternativo
            const alt = await findAlternativeDocId(docIdTrim);
            if (alt?.id) {
              const resAlt = await updateOrderDocument(alt.id, billingData, {
                index: i + idx,
                batchIndex: idx,
                bicciidRaw,
              });
              if (resAlt.ok) {
                statsRef.current.successAlt.push(
                  `${docIdTrim} -> ${alt.id} (${alt.byField})`
                );
                console.info(
                  `✔️ [OK-ALT] from=${docIdTrim} to=${alt.id} by=${alt.byField}`
                );
              } else {
                statsRef.current.altUpdateFailed.push({
                  from: docIdTrim,
                  to: alt.id,
                  code: resAlt.code,
                  msg: resAlt.msg,
                });
                console.error(
                  `❌ [ALT-UPDATE-ERR] from=${docIdTrim} to=${alt.id} code=${resAlt.code} msg=${resAlt.msg}`
                );
              }
            } else {
              statsRef.current.notFoundBoth.push(docIdTrim);
              console.error(
                `🚫 [NOT-FOUND-BOTH] id=${docIdTrim} (sin alt coincidencias)`
              );
            }
          } else {
            // Error distinto a not-found: deja rastro con ID y código
            statsRef.current.hardErrors.push({
              id: docIdTrim,
              code: res.code,
              msg: res.msg,
              index: i + idx,
            });
            console.error(
              `❌ [HARD-ERR] id=${docIdTrim} index=${i + idx} code=${
                res.code
              } msg=${res.msg}`
            );
          }

          console.groupEnd();
        });

        const settled = await Promise.allSettled(tasks);

        // Resumen por lote
        const rejected = settled.filter((s) => s.status === "rejected");
        if (rejected.length) {
          console.warn(`⚠️ [BATCH-REJECTED] i=${i} count=${rejected.length}`);
          rejected.forEach((r, k) =>
            console.warn(`  · reject[${k}]:`, r.reason)
          );
        } else {
          console.info(`✅ [BATCH-OK] i=${i} size=${batch.length}`);
        }

        const progreso = ((i + batch.length) / data.length) * 100;
        setProgress(progreso);
      }

      // Resumen global al terminar
      const s = statsRef.current;
      console.group("📊 Resumen de actualización Proforma");
      console.table([
        { tipo: "OK directos", total: s.successOriginal.length },
        { tipo: "OK por alternativo", total: s.successAlt.length },
        { tipo: "Not Found (solo original)", total: s.notFoundOriginal.length },
        {
          tipo: "Not Found (original y alternativo)",
          total: s.notFoundBoth.length,
        },
        { tipo: "Errores en alt update", total: s.altUpdateFailed.length },
        { tipo: "Errores distintos a not-found", total: s.hardErrors.length },
        {
          tipo: "invalid-argument (IDs inválidos)",
          total: s.invalidArgument.length,
        },
        { tipo: "BICCIID vacío/espacios", total: s.emptyId.length },
      ]);

      if (s.invalidArgument.length) {
        console.group("🔍 Detalle invalid-argument (primeros 200)");
        console.table(s.invalidArgument.slice(0, 200));
        console.log(
          "Índices (data):",
          s.invalidArgument.map((x) => x.index)
        );
        console.log("BICCIID crudos (únicos):", [
          ...new Set(s.invalidArgument.map((x) => String(x.bicciidRaw))),
        ]);
        console.groupEnd();
      }

      if (s.emptyId.length) {
        console.group("🔍 BICCIID vacíos (primeros 200)");
        console.table(s.emptyId.slice(0, 200));
        console.groupEnd();
      }

      if (s.notFoundBoth.length) {
        console.info(
          "IDs sin coincidencia (original ni alternativa):",
          s.notFoundBoth
        );
      }
      if (s.altUpdateFailed.length) {
        console.info("Fallos al actualizar alternativo:", s.altUpdateFailed);
      }
      if (s.hardErrors.length) {
        console.info("Errores no not-found:", s.hardErrors);
      }
      console.groupEnd();
    };

    processInBatches().catch((err) => {
      console.error("💥 Error procesando datos:", err);
    });

    return () => {
      isCancelled = true;
    };
  }, [data, isActiveUpdateProf]);

  return progressBilling;
};
